import { Scale } from "tonal";
import { nameToPitchClass } from "./notes";

export type ScaleType =
  | "major" | "minor" | "harmonic minor" | "melodic minor"
  | "major pentatonic" | "minor pentatonic" | "blues"
  | "dorian" | "phrygian" | "lydian" | "mixolydian" | "locrian";

export const SCALE_TYPES: readonly ScaleType[] = [
  "major", "minor", "harmonic minor", "melodic minor", "major pentatonic", "minor pentatonic",
  "blues", "dorian", "phrygian", "lydian", "mixolydian", "locrian",
];

export interface ScaleInfo {
  tonic: string;
  type: ScaleType;
  /** Spelled note names, e.g. G major → G A B C D E F#. */
  notes: string[];
  /** Pitch classes 0–11 in scale order. */
  pitchClasses: number[];
  /** Semitones above tonic for each degree. */
  semitones: number[];
}

/** Build a scale. Throws on unknown tonic. Spelling comes from tonal. */
export function getScale(tonic: string, type: ScaleType): ScaleInfo {
  const s = Scale.get(`${tonic} ${type}`);
  if (s.empty || s.notes.length === 0) throw new Error(`Unknown scale: ${tonic} ${type}`);
  const tonicPc = nameToPitchClass(tonic);
  if (tonicPc === null) throw new Error(`Unknown tonic: ${tonic}`);
  const pitchClasses = s.notes.map((n) => nameToPitchClass(n)!);
  return {
    tonic,
    type,
    notes: s.notes,
    pitchClasses,
    semitones: pitchClasses.map((pc) => (pc - tonicPc + 12) % 12),
  };
}

/** Step pattern between consecutive scale degrees, closing at the octave: W = whole, H = half, W+H = augmented second. */
export function stepPattern(semitones: readonly number[]): string[] {
  const all = [...semitones, 12];
  return all.slice(1).map((s, i) => {
    const d = s - all[i]!;
    return d === 1 ? "H" : d === 2 ? "W" : d === 3 ? "W+H" : `${d}`;
  });
}
